'use client'

import { useState, useEffect, Suspense } from 'react'
import { Plus, Lock } from 'lucide-react'
import { PiMagnifyingGlassLight } from 'react-icons/pi'
import Link from 'next/link'
import { useSearchParams, useRouter } from 'next/navigation'
import { CustomButton as Button } from '@/components/custom-button'
import { FeedPost } from '../feed-post'
import { ProfileFeedPostCard } from '../post/ProfileFeedPostCard'
import { useGetPostsLazyQuery, useListingDetailQuery } from 'graphql/generated/graphql'
import { getUserId } from '@/lib/utils'
import { Spinner } from 'flowbite-react'
import { HorizontalUpgradeBanner } from '../ui/upgrade-banner'
import { useSelector } from 'react-redux'
import { RootState } from 'features/store'
import { toast } from 'react-toastify'
import { isProfessionalTier } from '@/lib/tier-features'

function FeedPageInner({ userId, listingID, disabled, renderEmptyState, cardLayout = true }: { userId?: string | number, listingID?: number | null, disabled?: boolean, renderEmptyState?: (onAdd?: () => void) => React.ReactNode, cardLayout?: boolean }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [data, setData] = useState<any>(null)
  const [displayPosts, setDisplayPosts] = useState<any[]>([])
  const [cursor, setCursor] = useState<string | number | null>(null)
  const [initialLoadDone, setInitialLoadDone] = useState(false)
  const searchParams = useSearchParams()
  const router = useRouter()
  const empty = searchParams.get('empty') === 'true'
  const userID = getUserId()
  const isCurrentUser = String(userId) === String(userID)
  const user = useSelector((state: RootState) => state.culineerAuth.user)
  // Only the profile's listing scopes posts — never the viewer's listing
  const profileListingId = listingID || null
  // Tier/frequency checks only matter for the owner creating posts
  const ownerListingId = isCurrentUser
    ? (listingID || user?.default_listing_id || null)
    : profileListingId

  // Fetch fresh listing data directly from GraphQL instead of stale Redux
  const { data: listingData } = useListingDetailQuery({
    fetchPolicy: 'no-cache',
    variables: {
      listingId: ownerListingId,
    },
    skip: !ownerListingId,
  })

  // Frequency caps only apply to listing-backed (business) accounts
  const isVerifiedBusiness = Boolean(ownerListingId) && !isProfessionalTier(
    listingData?.listingDetail?.membership_type,
    listingData?.listingDetail?.enhanced
  )

  const lastPostDate = displayPosts[0]?.created_at || displayPosts[0]?.createdAt
  const canPost = !isVerifiedBusiness || !lastPostDate || getDaysSincePost(lastPostDate) >= 7
  const daysUntilNextPost = lastPostDate && isVerifiedBusiness ? Math.max(0, 7 - getDaysSincePost(lastPostDate)) : 0


  const [getPosts, { loading, fetchMore, error }] = useGetPostsLazyQuery({fetchPolicy: 'no-cache'})

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)
    return () => clearTimeout(timerId)
  }, [searchTerm])

  useEffect(() => {
    const getPostList = async () => {
      if (disabled) {
        setInitialLoadDone(true)
        return
      }
      try {
        const res = await getPosts({
          variables: {
            limit: 10,
            cursor: null,
            userID: userId || userID,
            listingID: profileListingId,
            group: 'user',
            searchTerm: debouncedSearchTerm || null,
          },
        })
        setData(res.data)
      } finally {
        setInitialLoadDone(true)
      }
    }

    getPostList()
  }, [disabled, userId, userID, profileListingId, debouncedSearchTerm, getPosts])

  // const { data, loading, error, fetchMore } = useGetPostsQuery({
  //   fetchPolicy: 'no-cache',
  //   variables: {
  //     limit: 10,
  //     cursor: null,
  //     userID: userId || userID,
  //     group: 'user',
  //     searchTerm: debouncedSearchTerm || null,
  //   },
  // })

  useEffect(() => {
    if (data?.getPosts) {
      setDisplayPosts(data.getPosts.posts)
      const posts = data.getPosts.posts
      setCursor(posts.length > 0 ? posts[posts.length - 1]?.id ?? null : null)
    }
  }, [data])

  useEffect(() => {
    if (empty) {
      setDisplayPosts([])
    } else if (data?.getPosts) {
      setDisplayPosts(data.getPosts.posts)
    }
  }, [empty, data])

  const loadMorePosts = async () => {
    await fetchMore({
      variables: {
        cursor: cursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        return {
          getPosts: {
            posts: [...prev.getPosts.posts, ...fetchMoreResult.getPosts.posts],
            totalCount: fetchMoreResult.getPosts.totalCount,
          },
        }
      },
    })
    const posts = data?.getPosts?.posts ?? []
    if (posts.length > 0) {
      setCursor(posts[posts.length - 1].id)
    }
  }

  if (error)
    return <ErrorMessage message={`Error loading posts: ${error.message}`} />

  const hasMorePosts = displayPosts.length < data?.getPosts?.totalCount


  if (!initialLoadDone || loading || disabled) {
    return (
      <div className='flex h-full pt-10 justify-center items-center w-full'>
        {disabled ? (
          <HorizontalUpgradeBanner />
        ):(
          <Spinner aria-label="loading" size="xl" color={'success'} />
        )}
      </div>
    )
  }

  return (
    <div className="min-h-screen py-4 sm:py-6">
      <div className="mx-auto">
        <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <h1 className="font-semibold">Feed Posts</h1>
            {isCurrentUser && (
              canPost ? (
                <Link
                  href="/create-post"
                  className="flex items-center gap-2 whitespace-nowrap rounded-full bg-primary px-3 py-1.5 text-white transition-colors hover:bg-[#75B339] sm:px-4 sm:py-2"
                >
                  <Plus className="h-4 w-4" />
                  <span className="text-sm">Create Post</span>
                </Link>
              ) : (
                <button
                  type="button"
                  className="flex cursor-not-allowed items-center gap-2 whitespace-nowrap rounded-full bg-gray-400 px-3 py-1.5 text-white opacity-70 sm:px-4 sm:py-2"
                  onClick={() => {
                    toast.info(`You can post again in ${daysUntilNextPost} day${daysUntilNextPost !== 1 ? 's' : ''}. Upgrade for unlimited posts!`, {
                      onClick: () => router.push('/pricing')
                    })
                  }}
                >
                  <Lock className="h-4 w-4 text-yellow-400" />
                  <span className="text-sm">Create Post</span>
                </button>
              )
            )}
          </div>
          <div className="relative xs:w-full sm:w-1/3">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
              <PiMagnifyingGlassLight size={24} color="#85C349" />
            </div>
            <input
              type="search"
              placeholder="Search posts"
              className="w-full rounded-full border border-[#CBD7DD] bg-white h-[50px] px-6 pl-9 pr-3 text-base font-medium text-[#16270C] placeholder-[#6B6B6B] placeholder:font-normal transition-all duration-100 ease-in-out placeholder:text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {!loading && data ? (
          <div className="w-full">
              {displayPosts.length > 0 ? (
                <>
                  <div className={cardLayout ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5' : 'space-y-6'}>
                    {displayPosts.map((post) =>
                      cardLayout ? (
                        <ProfileFeedPostCard
                          key={post.id}
                          post={post}
                          profileListing={
                            profileListingId
                              ? listingData?.listingDetail ?? null
                              : null
                          }
                        />
                      ) : (
                        <FeedPost key={post.id} post={post} />
                      )
                    )}
                  </div>
                  {hasMorePosts && (
                    <div className="mt-6 flex justify-center">
                      <Button
                        className="rounded-full bg-[#85C349] hover:bg-[#75B339]"
                        onClick={loadMorePosts}
                        disabled={loading}
                      >
                        {loading ? 'Loading...' : 'Load More'}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                (renderEmptyState ? renderEmptyState() : (
                  <div className="flex h-96">
                    <div className="flex flex-col items-center justify-center h-full w-full">
                      <img
                        src="/images/noFeed.svg"
                        style={{ filter: 'bgFill(100%)' }}
                        className="w-24 h-24 mb-6 opacity-60"
                      />
                      <h1 className="text-xl font-semibold mb-2 text-gray-800">
                        No <span className="text-[#6B6B6B]">Post's</span> Available
                      </h1>
                      <span className="text-sm text-[#4A4A4A] text-center max-w-sm mb-4">
                        No posts are available in your personal feed. Click on
                        &quot;Create Post&quot; to share something with your community.
                      </span>
                    </div>
                  </div>
                ))
              )}
          </div>
        ) : (
          <LoadingSpinner />
        )}
      </div>
    </div>
  )
}

export default function FeedPage(props: { userId?: string | number, listingID?: number | null, disabled?: boolean, renderEmptyState?: (onAdd?: () => void) => React.ReactNode, cardLayout?: boolean }) {
  return (
    <Suspense fallback={null}>
      <FeedPageInner {...props} />
    </Suspense>
  )
}

// Helper function to calculate days since post
function getDaysSincePost(postDate: string | Date): number {
  const now = new Date()
  const posted = new Date(postDate)
  const diffTime = Math.abs(now.getTime() - posted.getTime())
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
}

// Loading spinner component
const LoadingSpinner = () => (
  <div className="flex justify-center py-8">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#85C349] border-t-transparent"></div>
  </div>
)

// Error message component
const ErrorMessage = ({ message }: { message: string }) => (
  <div className="flex justify-center py-8 text-red-500">{message}</div>
)
